import axios from 'axios';

export const sanitize = (str: string): Sanitized => {
  return {
    sanitizedHTML: str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      // em タグのみ許可する
      .replace(/&lt;(\/)?em&gt;/gi, '<$1em>'),
  };
};

export const guardInvalidDate = (d: string): string => {
  // 月がないイレギュラーなデータの場合、APIから `2021/00` のように返却されるので月を除去
  return d.replace(/^(\d{4})\/00/, '$1');
};

export const searchUsers = async (query: string, page: number) => {
  const params: SearchParams = { query, page };
  const response = await axios.get<{ meta: ApiMeta; hits: ApiUser[] }>(
    `/api/v3/search/users`,
    {
      params,
    },
  );
  return fromApiSearchUsers(response.data, query);
};

export const fromApiSearchUsers = (
  data: {
    meta: ApiMeta;
    hits: ApiUser[];
  },
  query: string,
): {
  meta: Meta;
  users: User[];
  query: string;
} => {
  return {
    meta: fromApiMeta(data.meta),
    users: data.hits.map(fromApiUsers),
    query,
  };
};

const fromApiMeta = (meta: ApiMeta): Meta => ({
  totalCount: meta.total_count,
  currentPage: meta.current_page,
  totalPage: meta.total_page,
  countOnPage: meta.count_on_page,
  errorMessage: meta.error_message,
});

const fromApiUsers = (user: ApiUser): User => ({
  userId: user.user_id,
  oldKey: user.old_key,
  imageUrl: user.user_image_url || '/img/common/user_default.png',
  description: sanitize(user.description),
  topics: user.topics.map(fromApiTopic),
  jobs: user.jobs.map(fromApiCompany),
});

const fromApiTopic = (topic: ApiTopic): Topic => ({
  id: topic.id,
  title: sanitize(topic.title),
  description: sanitize(topic.description),
  price: topic.price,
});

const fromApiCompany = (company: ApiCompany): Company => ({
  id: company.id,
  name: sanitize(company.name),
  position: fromApiPosition(company.position),
});

const fromApiPosition = (position: ApiPosition): Position => ({
  id: position.id,
  department: sanitize(position.department),
  title: sanitize(position.title),
  startDate: guardInvalidDate(position.start_date),
  endDate: guardInvalidDate(position.end_date),
  isCurrent: position.is_current,
  description: sanitize(position.description),
});

/**
 * v-html を使用する際にエスケープされていることがわかるようにするための型
 */
export type Sanitized = {
  sanitizedHTML: string;
};

export type ApiMeta = {
  total_count: number;
  current_page: number;
  total_page: number;
  count_on_page: number;
  error_message: string;
};

export type ApiUser = {
  user_id: number;
  old_key: number;
  user_image_url: string | null;
  description: string;
  topics: ApiTopic[];
  jobs: ApiCompany[];
};

export type ApiTopic = {
  id: number;
  title: string;
  description: string;
  price: number | null;
};

export type ApiCompany = {
  id: number;
  name: string;
  position: ApiPosition;
};

export type ApiPosition = {
  id: number;
  department: string;
  title: string;
  start_date: string;
  end_date: string;
  is_current: boolean;
  description: string;
};

export type Meta = {
  totalCount: number;
  currentPage: number;
  totalPage: number;
  countOnPage: number;
  errorMessage: string;
};

export type User = {
  userId: number;
  oldKey: number;
  imageUrl: string;
  description: Sanitized;
  topics: Topic[];
  jobs: Company[];
};

export type Topic = {
  id: number;
  title: Sanitized;
  description: Sanitized;
  price: number | null;
};

export type Company = {
  id: number;
  name: Sanitized;
  position: Position;
};

export type Position = {
  id: number;
  department: Sanitized;
  title: Sanitized;
  startDate: string;
  endDate: string;
  isCurrent: boolean;
  description: Sanitized;
};

export type SearchParams = {
  query?: string;
  page?: number;
};
